/**
 * Plan Approval selection logic (PRD §17), kept pure so it is unit-tested:
 * - held episodes are visible but not selectable until the owner explicitly overrides the hold;
 * - renames and notes are sent only when meaningful;
 * - the owner may reorder the selected episodes (up/down); the order is sent as `order` only when it differs from the plan's.
 */
import { PlanSelection, type SeriesPlan } from "@contentops/contract";
import type { z } from "zod";

type PlanEpisode = SeriesPlan["episodes"][number];
export type PlanDraft = {
  selected: number[];
  overridden: number[];
  renames: Record<number, string>;
  notes: Record<number, string>;
  /** Every plan sequence in the owner's build/publish order (starts as plan order). Unselected ones keep their slot. */
  order: number[];
};

export function initDraft(plan: SeriesPlan): PlanDraft {
  return { selected: plan.episodes.filter((e) => !e.held).map((e) => e.sequence), overridden: [], renames: {}, notes: {}, order: plan.episodes.map((e) => e.sequence) };
}

export const isSelectable = (ep: PlanEpisode, d: PlanDraft) => !ep.held || d.overridden.includes(ep.sequence);
export const isSelected = (ep: PlanEpisode, d: PlanDraft) => d.selected.includes(ep.sequence) && isSelectable(ep, d);

export function toggle(d: PlanDraft, ep: PlanEpisode): PlanDraft {
  if (!isSelectable(ep, d)) return d;
  const on = d.selected.includes(ep.sequence);
  return { ...d, selected: on ? d.selected.filter((s) => s !== ep.sequence) : [...d.selected, ep.sequence] };
}

/** Explicit, confirmed override of a Studio hold: the episode becomes selectable and is selected. */
export function overrideHold(d: PlanDraft, ep: PlanEpisode): PlanDraft {
  if (!ep.held || d.overridden.includes(ep.sequence)) return d;
  return { ...d, overridden: [...d.overridden, ep.sequence], selected: [...new Set([...d.selected, ep.sequence])] };
}

export function restoreHold(d: PlanDraft, ep: PlanEpisode): PlanDraft {
  return { ...d, overridden: d.overridden.filter((s) => s !== ep.sequence), selected: d.selected.filter((s) => s !== ep.sequence) };
}

export function selectAll(d: PlanDraft, plan: SeriesPlan): PlanDraft {
  return { ...d, selected: plan.episodes.filter((e) => isSelectable(e, d)).map((e) => e.sequence) };
}
export const selectNone = (d: PlanDraft): PlanDraft => ({ ...d, selected: [] });

export const rename = (d: PlanDraft, seq: number, title: string): PlanDraft => ({ ...d, renames: { ...d.renames, [seq]: title } });
export const setNote = (d: PlanDraft, seq: number, note: string): PlanDraft => ({ ...d, notes: { ...d.notes, [seq]: note } });

export function effectiveTitle(ep: PlanEpisode, d: PlanDraft): string {
  const r = d.renames[ep.sequence]?.trim();
  return r || ep.title;
}

/** The selected episodes in the owner's order. */
export function selectedEpisodes(plan: SeriesPlan, d: PlanDraft): PlanEpisode[] {
  const bySeq = new Map(plan.episodes.map((e) => [e.sequence, e]));
  const ordered = [...d.order, ...plan.episodes.map((e) => e.sequence).filter((s) => !d.order.includes(s))];
  return ordered.map((s) => bySeq.get(s)).filter((e): e is PlanEpisode => !!e && isSelected(e, d));
}

/** Moves a selected episode one place up (-1) or down (+1) among the SELECTED episodes. */
export function move(plan: SeriesPlan, d: PlanDraft, seq: number, dir: -1 | 1): PlanDraft {
  const chosen = selectedEpisodes(plan, d).map((e) => e.sequence);
  const i = chosen.indexOf(seq);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= chosen.length) return d;
  const other = chosen[j]!;
  const order = [...d.order];
  const a = order.indexOf(seq), b = order.indexOf(other);
  if (a < 0 || b < 0) return d;
  [order[a], order[b]] = [order[b]!, order[a]!];
  return { ...d, order };
}

/** Puts the selected episodes back in plan order. */
export const resetOrder = (plan: SeriesPlan, d: PlanDraft): PlanDraft => ({ ...d, order: plan.episodes.map((e) => e.sequence) });

export function isReordered(plan: SeriesPlan, d: PlanDraft): boolean {
  const chosen = selectedEpisodes(plan, d).map((e) => e.sequence);
  const planOrder = plan.episodes.filter((e) => isSelected(e, d)).map((e) => e.sequence);
  return chosen.some((s, i) => s !== planOrder[i]);
}

export type SelectionResult = { ok: true; value: z.output<typeof PlanSelection> } | { ok: false; error: string };

export function toSelection(plan: SeriesPlan, planVersion: number, d: PlanDraft): SelectionResult {
  const chosen = selectedEpisodes(plan, d);
  if (!chosen.length) return { ok: false, error: "Select at least one episode." };
  const renames: Record<string, string> = {};
  const notes: Record<string, string> = {};
  for (const ep of chosen) {
    const r = d.renames[ep.sequence]?.trim();
    if (r && r !== ep.title) renames[String(ep.sequence)] = r;
    const n = d.notes[ep.sequence]?.trim();
    if (n) notes[String(ep.sequence)] = n;
  }
  const selected_sequences = plan.episodes.filter((e) => isSelected(e, d)).map((e) => e.sequence);
  const order = isReordered(plan, d) ? chosen.map((e) => e.sequence) : undefined;
  const parsed = PlanSelection.safeParse({ plan_version: planVersion, selected_sequences, ...(order ? { order } : {}), renames, notes });
  return parsed.success ? { ok: true, value: parsed.data } : { ok: false, error: parsed.error.issues[0]?.message ?? "Invalid selection" };
}
